// frontend/src/pages/AuditSnapshot.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import { getAuditSnapshotPayload } from "../lib/auditHistory";
import { getCachedFxRates, convertMoney, guessCurrencySymbol } from "../lib/fxRates";
import { ArrowLeft, Info, ChevronDown, ChevronUp, FileText, Scale } from "lucide-react";

function cn(...xs) {
  return xs.filter(Boolean).join(" ");
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(String(iso));
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

function fmtMoney(x, sym) {
  const v = Number(x);
  if (!Number.isFinite(v)) return "—";
  return `${sym || "$"}${Math.round(v).toLocaleString("en-US")}`;
}

function safeObj(x) {
  return x && typeof x === "object" ? x : {};
}

// AuditSnapshot-only primary currency preference (separate from Reports)
const PRIMARY_CCY_KEY = "aifine_auditsnapshot_primary_currency_v1";

export default function AuditSnapshot() {
  const navigate = useNavigate();
  const { snapshotId } = useParams();

  const [showTechnical, setShowTechnical] = useState(false);

  // Primary currency selection (persisted)
  const [primaryCurrency, setPrimaryCurrency] = useState(() => {
    try {
      return localStorage.getItem(PRIMARY_CCY_KEY) || "";
    } catch {
      return "";
    }
  });

  // FX cache state (Phase 1)
  const [fx, setFx] = useState(() => ({
    status: "idle", // idle | loading | ready | error
    cache: null,
    error: null,
  }));

  // Persist primary currency selection
  useEffect(() => {
    try {
      if (primaryCurrency) localStorage.setItem(PRIMARY_CCY_KEY, primaryCurrency);
    } catch {
      // ignore
    }
  }, [primaryCurrency]);

  const payload = useMemo(() => {
    if (!snapshotId) return null;
    try {
      return getAuditSnapshotPayload(snapshotId);
    } catch {
      return null;
    }
  }, [snapshotId]);

  const entityName = payload?.entity_name || "Not specified";
  const createdAtIso = payload?.created_at_iso || null;
  const createdAt = fmtDateTime(createdAtIso);

  const summary = safeObj(payload?.summary || null);
  const assessment = safeObj(payload?.assessment || null);

  const scopeArr = Array.isArray(summary?.jurisdictions) ? summary.jurisdictions : [];
  const scopeText = scopeArr.length ? scopeArr.join(", ") : "—";

  // Snapshot-driven exposure only
  const exposure = payload?.risk_exposure || null;
  const perRegime = exposure?.per_regime || {};
  const combinedByCurrency = exposure?.combined_by_currency || null;
  const engineHeadline = exposure?.headline || null;

  const currencyCodes = useMemo(() => {
    const m = combinedByCurrency && typeof combinedByCurrency === "object" ? combinedByCurrency : {};
    return Object.keys(m || {}).filter(Boolean);
  }, [combinedByCurrency]);

  const engineHeadlineCode = engineHeadline?.currency_code || "";
  const chosenPrimaryCode = useMemo(() => {
    if (primaryCurrency && currencyCodes.includes(primaryCurrency)) return primaryCurrency;
    if (engineHeadlineCode && currencyCodes.includes(engineHeadlineCode)) return engineHeadlineCode;
    return currencyCodes[0] || "";
  }, [primaryCurrency, currencyCodes, engineHeadlineCode]);

  const primarySymbol = useMemo(() => {
    const b = combinedByCurrency && chosenPrimaryCode ? combinedByCurrency[chosenPrimaryCode] : null;
    const sym =
      b?.currency_symbol ||
      engineHeadline?.currency_symbol ||
      summary?.currency_symbol ||
      null;
    return sym || guessCurrencySymbol(chosenPrimaryCode);
  }, [combinedByCurrency, chosenPrimaryCode, engineHeadline, summary]);

  const hasExposureNativePrimaryBucket = useMemo(() => {
    if (!combinedByCurrency || !chosenPrimaryCode) return false;
    const b = combinedByCurrency[chosenPrimaryCode];
    return Boolean(
      b &&
        Number.isFinite(Number(b?.combined_structured_worst_case_native)) &&
        Number(b?.combined_structured_worst_case_native) > 0
    );
  }, [combinedByCurrency, chosenPrimaryCode]);

  // Secondary buckets (everything not primary)
  const secondaryBuckets = useMemo(() => {
    const m = combinedByCurrency && typeof combinedByCurrency === "object" ? combinedByCurrency : {};
    const codes = Object.keys(m || {}).filter(Boolean);
    return codes
      .filter((c) => c !== chosenPrimaryCode)
      .map((code) => ({ code, bucket: m[code] }))
      .filter((x) => x.bucket && typeof x.bucket === "object");
  }, [combinedByCurrency, chosenPrimaryCode]);

  // Load FX cache only when multi-currency and a primary chosen
  useEffect(() => {
    const shouldUseFx = currencyCodes.length > 1 && Boolean(chosenPrimaryCode);
    if (!shouldUseFx) {
      setFx((s) => ({ ...s, status: "idle", error: null }));
      return;
    }

    let alive = true;
    (async () => {
      try {
        setFx({ status: "loading", cache: null, error: null });
        const cache = await getCachedFxRates({ maxAgeHours: 12 });
        if (!alive) return;
        setFx({ status: "ready", cache, error: null });
      } catch (err) {
        if (!alive) return;
        setFx({ status: "error", cache: null, error: String(err?.message || err || "FX error") });
      }
    })();

    return () => {
      alive = false;
    };
  }, [currencyCodes.length, chosenPrimaryCode]);

  const fxEnabled = currencyCodes.length > 1 && fx.status === "ready" && fx.cache && chosenPrimaryCode;

  const headlineConverted = useMemo(() => {
    const out = {
      ok: false,
      worst: null,
      net: null,
      floor: null,
      missing: [],
    };

    if (!combinedByCurrency || !chosenPrimaryCode) return out;
    if (!fxEnabled) return out;

    const cache = fx.cache;

    let worst = 0;
    let net = 0;
    let floor = 0;
    const missing = new Set();

    Object.keys(combinedByCurrency || {}).forEach((code) => {
      const b = combinedByCurrency[code];
      if (!b) return;

      const w = Number(b?.combined_structured_worst_case_native);
      const n = Number(b?.combined_net_native);
      const f = Number(b?.combined_floor_native);

      const cw = Number.isFinite(w) ? convertMoney(w, code, chosenPrimaryCode, cache) : null;
      const cn = Number.isFinite(n) ? convertMoney(n, code, chosenPrimaryCode, cache) : null;
      const cf = Number.isFinite(f) ? convertMoney(f, code, chosenPrimaryCode, cache) : null;

      if (Number.isFinite(w) && cw == null) missing.add(String(code));
      if (Number.isFinite(n) && cn == null) missing.add(String(code));
      if (Number.isFinite(f) && cf == null) missing.add(String(code));

      if (typeof cw === "number") worst += cw;
      if (typeof cn === "number") net += cn;
      if (typeof cf === "number") floor += cf;
    });

    out.ok = true;
    out.worst = Math.round(worst);
    out.net = Math.round(net);
    out.floor = Math.round(floor);
    out.missing = Array.from(missing);
    return out;
  }, [combinedByCurrency, chosenPrimaryCode, fxEnabled, fx.cache]);

  const showHeadlineFx =
    fxEnabled && headlineConverted.ok && typeof headlineConverted.worst === "number";

  const scorePct =
    typeof assessment?.compliance_score_pct === "number"
      ? Math.max(0, Math.min(100, Math.round(assessment.compliance_score_pct)))
      : null;

  const failed =
    typeof assessment?.failed_count === "number"
      ? assessment.failed_count
      : Array.isArray(assessment?.failed_question_ids)
      ? assessment.failed_question_ids.length
      : 0;

  const unsure =
    typeof assessment?.unsure_count === "number"
      ? assessment.unsure_count
      : Array.isArray(assessment?.unsure_question_ids)
      ? assessment.unsure_question_ids.length
      : 0;

  const total =
    typeof assessment?.total_questions === "number"
      ? assessment.total_questions
      : Array.isArray(assessment?.active_question_ids)
      ? assessment.active_question_ids.length
      : 60;

  const passed = Math.max(0, Number(total) - Number(failed) - Number(unsure));

  const [showExplain, setShowExplain] = useState(false);

  const explainText = {
    worst:
      "Structured Worst Case is the model’s conservative headline estimate for this saved snapshot, bounded by statutory ceilings. It is a planning number, not legal advice.",
    net:
      "Net Planning Case is a discounted planning estimate that assumes practical reducing factors often present in enforcement (e.g., scope narrowing, remediation, cooperation, governance maturity, counsel strategy).",
    floor:
      "Planning Floor is a lower planning anchor used for budget and prioritization. Real outcomes can be above or below this depending on facts, enforcement posture, and jurisdiction.",
    fx:
      "Multi-currency snapshots store native totals per currency. If you choose a primary currency, the top summary converts totals using cached FX rates with timestamped disclosure. Converted totals are planning-only.",
  };

  return (
    <div className="flex h-screen bg-slate-50 text-slate-900 overflow-hidden">
      <Sidebar />

      <main className="flex-1 overflow-auto p-8 md:p-12">
        <div className="mx-auto w-full max-w-6xl">
          {/* Header */}
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="text-3xl font-extrabold tracking-tight">
                Assessment Audit Snapshot
              </div>
              <div className="mt-1 text-sm text-slate-600">
                Immutable point-in-time record of a completed Assessment Audit.
              </div>

              <div className="mt-4 flex flex-wrap gap-2 text-xs">
                <span className="rounded-full border bg-white px-3 py-1">
                  Entity: <span className="font-bold">{entityName}</span>
                </span>
                <span className="rounded-full border bg-white px-3 py-1">
                  Date: <span className="font-bold">{createdAt}</span>
                </span>
                <span className="rounded-full border bg-white px-3 py-1">
                  Scope: <span className="font-bold">{scopeText}</span>
                </span>
                <span className="rounded-full border bg-white px-3 py-1 inline-flex items-center gap-2">
                  <Scale size={14} />
                  Immutable snapshot
                </span>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => navigate("/reports")}
                className="inline-flex items-center gap-2 rounded-2xl border bg-white px-4 py-2 text-sm font-black hover:bg-slate-50"
              >
                <ArrowLeft size={16} />
                Back to Reports
              </button>
            </div>
          </div>

          {/* Missing payload */}
          {!payload ? (
            <div className="mt-8 rounded-3xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-950">
              Snapshot not found (missing payload in localStorage).
            </div>
          ) : (
            <>
              {/* Summary row */}
              <div className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-3 items-stretch">
                <div className="h-full rounded-3xl border border-slate-200 bg-white p-6 shadow-sm lg:col-span-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-black text-slate-800">
                      <FileText size={14} />
                      Exposure (from stored snapshot)
                    </div>

                    <button
                      type="button"
                      onClick={() => setShowExplain((v) => !v)}
                      className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-black hover:bg-slate-50"
                      title="Show / hide what these numbers mean"
                    >
                      <Info size={14} />
                      What do these mean?
                      {showExplain ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </button>
                  </div>

                  {/* Currency controls */}
                  {currencyCodes.length > 1 ? (
                    <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
                      <span className="rounded-full border bg-white px-3 py-1">
                        Primary currency: <span className="font-black">{chosenPrimaryCode}</span>
                      </span>

                      <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-amber-900 font-black">
                        Multi-currency
                      </span>

                      <div className="ml-0 md:ml-2 inline-flex items-center gap-2">
                        <span className="text-slate-600 font-bold">Choose primary currency:</span>
                        <select
                          value={
                            primaryCurrency && currencyCodes.includes(primaryCurrency)
                              ? primaryCurrency
                              : chosenPrimaryCode
                          }
                          onChange={(e) => setPrimaryCurrency(String(e.target.value || ""))}
                          className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-black"
                        >
                          {currencyCodes.map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>

                        <span className="text-slate-500">
                          {fx.status === "loading"
                            ? "Loading FX…"
                            : fx.status === "error"
                            ? "FX unavailable (showing native only)."
                            : "Headline totals include FX conversion."}
                        </span>
                      </div>
                    </div>
                  ) : currencyCodes.length === 1 ? (
                    <div className="mt-4 text-xs text-slate-500">
                      Currency: <span className="font-black">{currencyCodes[0]}</span>
                    </div>
                  ) : null}

                  {/* Headline cards */}
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="text-[11px] font-black uppercase tracking-wide text-slate-500">
                        Structured Worst Case
                      </div>
                      <div className="mt-2 text-2xl font-black text-red-700">
                        {showHeadlineFx
                          ? fmtMoney(headlineConverted.worst, primarySymbol)
                          : hasExposureNativePrimaryBucket
                          ? fmtMoney(
                              combinedByCurrency?.[chosenPrimaryCode]?.combined_structured_worst_case_native,
                              primarySymbol
                            )
                          : "—"}
                      </div>
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="text-[11px] font-black uppercase tracking-wide text-slate-500">
                        Net Planning Case
                      </div>
                      <div className="mt-2 text-2xl font-black text-blue-600">
                        {showHeadlineFx
                          ? fmtMoney(headlineConverted.net, primarySymbol)
                          : hasExposureNativePrimaryBucket
                          ? fmtMoney(
                              combinedByCurrency?.[chosenPrimaryCode]?.combined_net_native,
                              primarySymbol
                            )
                          : "—"}
                      </div>
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="text-[11px] font-black uppercase tracking-wide text-slate-500">
                        Planning Floor
                      </div>
                      <div className="mt-2 text-2xl font-black text-blue-600">
                        {showHeadlineFx
                          ? fmtMoney(headlineConverted.floor, primarySymbol)
                          : hasExposureNativePrimaryBucket
                          ? fmtMoney(
                              combinedByCurrency?.[chosenPrimaryCode]?.combined_floor_native,
                              primarySymbol
                            )
                          : "—"}
                      </div>
                    </div>
                  </div>

                  {/* FX disclosure */}
                  {currencyCodes.length > 1 ? (
                    <div className="mt-3 text-[11px] text-slate-500">
                      {fx.status === "ready" && fx.cache ? (
                        <>
                          <span className="font-black text-slate-700">FX disclosure:</span>{" "}
                          Headline totals convert multi-currency totals into{" "}
                          <span className="font-black">{chosenPrimaryCode}</span> using cached rates from{" "}
                          <span className="font-black">{fx.cache.source_label}</span> as of{" "}
                          <span className="font-black">{fmtDateTime(fx.cache.as_of_iso)}</span>. Planning
                          only; real enforcement outcomes vary.
                          {headlineConverted?.missing?.length ? (
                            <>
                              {" "}
                              <span className="text-amber-700 font-black">Note:</span> Missing FX rates for:{" "}
                              <span className="font-black">{headlineConverted.missing.join(", ")}</span>. Those
                              currencies are excluded from converted headline totals.
                            </>
                          ) : null}
                        </>
                      ) : fx.status === "error" ? (
                        <>
                          <span className="font-black text-slate-700">FX disclosure:</span>{" "}
                          FX conversion unavailable right now, so headline totals reflect native totals only
                          (no conversion). Planning only.
                        </>
                      ) : (
                        <>
                          <span className="font-black text-slate-700">FX disclosure:</span> Loading FX rates…
                        </>
                      )}
                    </div>
                  ) : null}

                  {/* Explain */}
                  {showExplain ? (
                    <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4 text-xs text-slate-700 space-y-2">
                      <div>
                        <span className="font-black text-slate-900">Structured Worst Case:</span>{" "}
                        {explainText.worst}
                      </div>
                      <div>
                        <span className="font-black text-slate-900">Net Planning Case:</span>{" "}
                        {explainText.net}
                      </div>
                      <div>
                        <span className="font-black text-slate-900">Planning Floor:</span>{" "}
                        {explainText.floor}
                      </div>
                      <div className="text-slate-600">
                        <span className="font-black text-slate-900">Currency note:</span>{" "}
                        {explainText.fx}
                      </div>
                    </div>
                  ) : (
                    <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4 text-xs text-slate-600">
                      <span className="font-black text-slate-900">Note:</span>{" "}
                      Net/Floor are conservative planning factors. Many real-world factors (scope narrowing,
                      mitigation, cooperation, governance maturity, and counsel strategy) can reduce exposure.
                    </div>
                  )}

                  {/* Secondary currency totals */}
                  {secondaryBuckets.length ? (
                    <div className="mt-5 rounded-3xl border border-slate-200 bg-white p-5">
                      <div className="text-[11px] font-black uppercase tracking-[0.35em] text-slate-500">
                        Secondary Currency Totals
                      </div>
                      <div className="mt-1 text-xs text-slate-500">
                        Native totals per currency. Converted preview is informational only.
                      </div>

                      <div className="mt-4 space-y-2">
                        {secondaryBuckets.map(({ code, bucket }) => {
                          const sym2 = bucket?.currency_symbol || guessCurrencySymbol(code) || "$";

                          const w = Number(bucket?.combined_structured_worst_case_native);
                          const n = Number(bucket?.combined_net_native);
                          const f = Number(bucket?.combined_floor_native);

                          const convW =
                            fxEnabled && Number.isFinite(w)
                              ? convertMoney(w, code, chosenPrimaryCode, fx.cache)
                              : null;
                          const convN =
                            fxEnabled && Number.isFinite(n)
                              ? convertMoney(n, code, chosenPrimaryCode, fx.cache)
                              : null;
                          const convF =
                            fxEnabled && Number.isFinite(f)
                              ? convertMoney(f, code, chosenPrimaryCode, fx.cache)
                              : null;

                          const hasConv =
                            typeof convW === "number" ||
                            typeof convN === "number" ||
                            typeof convF === "number";

                          return (
                            <div
                              key={code}
                              className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3"
                            >
                              <div className="inline-flex items-center gap-2">
                                <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-black">
                                  {code}
                                </span>
                                <span className="text-xs text-slate-500 font-bold">Native</span>
                              </div>

                              <div className="text-sm md:text-base font-black flex flex-wrap items-center gap-4">
                                <span className="text-slate-800">
                                  Worst: <span className="text-red-700">{fmtMoney(w, sym2)}</span>
                                </span>
                                <span className="text-slate-800">
                                  Net: <span className="text-blue-600">{fmtMoney(n, sym2)}</span>
                                </span>
                                <span className="text-slate-800">
                                  Floor: <span className="text-blue-600">{fmtMoney(f, sym2)}</span>
                                </span>
                              </div>

                              {currencyCodes.length > 1 ? (
                                <div className="w-full text-[11px] text-slate-500">
                                  {fx.status === "ready" && hasConv ? (
                                    <>
                                      Converted to <span className="font-black">{chosenPrimaryCode}</span>:{" "}
                                      <span className="text-slate-700 font-black">
                                        Worst {fmtMoney(convW, primarySymbol)}
                                      </span>{" "}
                                      •{" "}
                                      <span className="text-slate-700 font-black">
                                        Net {fmtMoney(convN, primarySymbol)}
                                      </span>{" "}
                                      •{" "}
                                      <span className="text-slate-700 font-black">
                                        Floor {fmtMoney(convF, primarySymbol)}
                                      </span>{" "}
                                      <span className="text-slate-400">(planning only)</span>
                                    </>
                                  ) : fx.status === "loading" ? (
                                    <>Converted preview: loading FX…</>
                                  ) : fx.status === "error" ? (
                                    <>Converted preview unavailable (FX error).</>
                                  ) : (
                                    <>Converted preview unavailable.</>
                                  )}
                                </div>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}

                  {/* Per-regime lines (native) */}
                  {exposure ? (
                    <div className="mt-5 space-y-2">
                      {Object.keys(perRegime).map((k) => {
                        const e = perRegime[k];
                        const sym3 = e.currency_symbol || guessCurrencySymbol(e.currency_code) || "$";
                        const isOverlay = Boolean(e?.inferred_overlay);

                        return (
                          <div
                            key={k}
                            className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white px-4 py-3"
                          >
                            <div>
                              <div className="text-sm font-black inline-flex items-center gap-2">
                                {e.display_name || k}
                                {isOverlay ? (
                                  <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-black text-amber-900">
                                    Inferred overlay
                                  </span>
                                ) : null}
                              </div>
                              <div className="text-xs text-slate-500">
                                Ceiling: {fmtMoney(e.statutory_ceiling_native, sym3)}
                              </div>
                            </div>

                            <div className="text-sm font-black">
                              {fmtMoney(e.structured_worst_case_native, sym3)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                </div>

                <div className="h-full rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                  <div className="text-xs font-black uppercase tracking-wide text-slate-500">
                    Findings
                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-3">
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="text-[11px] font-black uppercase tracking-wide text-slate-500">
                        Compliance score
                      </div>
                      <div className="mt-2 text-3xl font-black text-slate-900">
                        {scorePct == null ? "—" : `${scorePct}%`}
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                      <div className="rounded-2xl border border-red-200 bg-red-50 p-4">
                        <div className="text-[11px] font-black uppercase tracking-wide text-red-700">
                          Failed
                        </div>
                        <div className="mt-1 text-2xl font-black text-red-700">{failed}</div>
                      </div>

                      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
                        <div className="text-[11px] font-black uppercase tracking-wide text-amber-800">
                          Unsure
                        </div>
                        <div className="mt-1 text-2xl font-black text-amber-800">{unsure}</div>
                      </div>

                      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                        <div className="text-[11px] font-black uppercase tracking-wide text-emerald-800">
                          Passed
                        </div>
                        <div className="mt-1 text-2xl font-black text-emerald-800">{passed}</div>
                      </div>
                    </div>

                    <div className="text-xs text-slate-500">
                      This snapshot is frozen and will not change if you edit answers later.
                    </div>
                  </div>
                </div>
              </div>

              {/* Technical details (collapsed by default) */}
              <div className="mt-8 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <button
                  type="button"
                  onClick={() => setShowTechnical((v) => !v)}
                  className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-xs font-black hover:bg-slate-50"
                  title="Show / hide technical snapshot payload"
                >
                  <Info size={14} />
                  Technical details
                  {showTechnical ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>

                {showTechnical ? (
                  <div className="mt-4">
                    <div className="mb-3 text-xs text-slate-600">
                      Snapshot ID:{" "}
                      <span className="font-mono font-black">{String(snapshotId || "")}</span>
                    </div>
                    <pre className="max-h-[65vh] overflow-auto rounded-2xl border border-slate-200 bg-slate-50 p-4 text-xs">
                      {JSON.stringify(payload, null, 2)}
                    </pre>
                  </div>
                ) : (
                  <div className="mt-3 text-xs text-slate-500">
                    Hidden by default. Intended for advanced review and debugging.
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="mx-auto w-full mt-10 pb-8 text-xs text-slate-400">
                © 2026 Advanced Advisory Services, LLC. All Rights Reserved.
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}